import {
  Box,
  useTheme,
  Avatar,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { tokens } from '../../global/theme/tokens';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExoTable from '../../components/exo/ExoTable';
import { API_GET, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { InvoiceStatus } from '../../components/special/Status';
import ExtendInvoice from './ExtendInvoice';
import Header from '../../components/special/Header';
import ExoTableRecipient from '../../components/exo/ExoTableRecipient';
import NewInvoice from '../../components/new/NewInvoice';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../components/label/MoneyLabel';
import useFetch from '../../api/useFetch';
import { useNavigate } from 'react-router-dom';
import { recipientTypeMap } from '../../components/routing/routingMaps';
import DateLabel from '../../components/label/DateLabel';
import { LeadStatusSelector } from '../leads/status/LeadStatusSelector';
import { updateOrCreate } from '../../components/special/updateOrCreate';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import DocumentOverviewCard from '../../components/document-overview/DocumentOverviewCard';
import { buildPermanentFilter } from '../../components/document-overview/buildPermanentFilter';

const Invoices = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const navigate = useNavigate();

  // null until the overview card reports its (persisted) filter — prevents a
  // double table fetch (unfiltered then filtered) on initial load.
  const [overviewFilters, setOverviewFilters] = useState(null);
  const permanentFilter = overviewFilters
    ? buildPermanentFilter({ ...overviewFilters, dateField: 'date' })
    : '';
  const fetchPath =
    overviewFilters === null
      ? null
      : permanentFilter
        ? `invoices?${permanentFilter}`
        : 'invoices';

  const { apiData, isLoading, setApiData } = useFetch(fetchPath);

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleStatusClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    //handleChange(status.status, item);

    const request = { status: status.status };

    DBRequest({
      config,
      path: `invoices/${item.id}/status`,
      method: API_PATCH(request),
      onResponse: () => {
        setApiData({
          ...apiData,
          data: updateOrCreate(apiData.data, {
            ...item,
            status: status.status,
          }),
        });
        //setInvoice({ ...invoice, status: newStatus });
        //onUpdate({ ...row, status: newStatus });
      },
      //onError: () => setStatus(row.status),
      //onFeedback: handleAlertOpen,
    });
  }

  // table
  const Columns = [
    {
      header: t('Nr'), // header of column
      key: 'nr', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Box className="monospace whitespace-nowrap">
          {row.nr ?? t('Draft')}
        </Box>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Status'),
      key: 'status',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <InvoiceStatus
          number={row.status}
          onClick={(e) => handleStatusClick(e, row)}
        ></InvoiceStatus>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Date'),
      key: 'date',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      cell: ({ row }) => <DateLabel date={row.date}></DateLabel>,
    },
    {
      header: t('Title'),
      key: 'title',
      width: '100%',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      onClick: (row) => navigate(`/invoices/${row.id}`),
    },
    {
      header: t('Project'),
      key: 'project',
      thAlign: 'left',
      tdAlign: 'left',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Box className="whitespace-nowrap">
          {row.project ? `${row.project.nr ?? ''} ${row.project.name}`.trim() : ''}
        </Box>
      ),
      onClick: (row) => row.project && navigate(`/projects/${row.project.id}`),
    },
    {
      header: t('Client'),
      key: 'recipient',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      width: '270px',
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      cell: ({ row }) => <ExoTableRecipient row={row} />, // custom xml for cells in the column. you get the row as object
      onClick: (row) =>
        navigate(`/${recipientTypeMap[row.recipientType]}/${row.recipient.id}`),
    },
    {
      header: t('netto'),
      key: 'amount',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.amount} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('tax'),
      key: 'taxVolume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.taxAmount} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('brutto'),
      key: 'volume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.taxAmount + row.amount} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('skonto'),
      key: 'skonto',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      className: 'w-full lg:w-auto',
      cell: ({ row }) => {
        const brutto = row.taxAmount + row.amount;
        return (
          <MoneyLabel
            money={
              row.discount
                ? brutto - brutto * (parseFloat(row.discountRate) / 100)
                : brutto
            }
          />
        );
      },
      sortable: true, // enable sort function for column
    },
    {
      header: t('Valid Until'),
      key: 'validUntil',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <DateLabel date={row.validUntil} />,
      sortable: true, // enable sort function for column
    },
    {
      header: '',
      key: 'link',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      className: 'w-full lg:w-auto',
      cell: ({ row }) => (
        <Tooltip title={t('Open in new window')}>
          <Link href={`/invoices/${row.id}`} target="_blank">
            <IconButton>
              <OpenInBrowserIcon />
            </IconButton>
          </Link>
        </Tooltip>
      ),
    },
  ];

  const filters = {
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'dropdown',
          label: t('Status'),
          key: 'state2',
          baseValue: 'status[eq]=',
          active: false,
          options: [
            {
              label: t('Created'),
              value: 0,
            },
            {
              label: t('Payed'),
              value: 1,
            },
            {
              label: t('Sent'),
              value: 2,
            },
            {
              label: t('In Arrears'),
              value: 3,
            },
            {
              label: t('Refusal to Pay'),
              value: 4,
            },
          ],
        },
        {
          type: 'dropdown',
          label: t('Drafts'),
          key: 'isDraft',
          baseValue: 'isDraft[eq]=',
          active: false,
          options: [
            {
              label: t('Invoices'),
              value: 0,
            },
            {
              label: t('Drafts'),
              value: 1,
            },
          ],
        },
      ],
    },
  };

  const config = useConfig();

  // new invoice
  const [openNew, setOpenNew] = useState(false);
  const handleClickNewInvoice = (event, reason) => {
    setOpenNew(!openNew);
  };

  return (
    <>
      <Header title={t('Invoices')} />

      <DocumentOverviewCard
        resource="invoices"
        preferencesKey="invoices"
        onFilterChange={setOverviewFilters}
        actions={[
          {
            primary: true,
            label: t('New Invoice'),
            icon: <NoteAddIcon fontSize="small" />,
            onClick: handleClickNewInvoice,
          },
        ]}
      />

      <ExoTable
        data={apiData}
        isLoading={!Boolean(apiData)}
        columns={Columns}
        extendElement={({ row, onUpdate, onDelete }) => (
          <ExtendInvoice row={row} onUpdate={onUpdate} onDelete={onDelete} />
        )}
        onDataChange={setApiData}
        filter={permanentFilter}
        filters={filters}
        columnSettingsKey="invoices"
        defaultHiddenColumns={[
          'amount',
          'taxVolume',
          'volume',
          'validUntil',
          'skonto',
          'project',
        ]}
      />
      <NewInvoice
        open={openNew}
        onClose={() => setOpenNew(false)}
        invoices={apiData}
        setInvoices={setApiData}
      />
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
        customStatuses={[
          {
            label: t('Created'),
            status: 0,
            variant: t('open'),
          },
          {
            label: t('Sent'),
            status: 2,
            variant: t('sent'),
          },
          {
            label: t('Payed'),
            status: 1,
            variant: t('accepted'),
          },
          {
            label: t('In Arrears'),
            status: 3,
            variant: t('expired'),
          },
          {
            label: t('Refusal to Pay'),
            status: 4,
            variant: t('rejected'),
          },
          {
            label: t('Invoice cancelled'),
            status: 5,
            variant: t('rejected'),
          },
        ]}
      />
    </>
  );
};

export default Invoices;
